import axios from "axios";

const DEFAULT_GET_CACHE_TTL_MS = 30_000;
const SAFE_METHODS = new Set(["get", "head", "options"]);
const responseCache = new Map();

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
};

const sortValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }

  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortValue(value[key]);
        return acc;
      }, {});
  }

  return value;
};

const buildCacheKey = (config) => {
  const method = (config.method || "get").toLowerCase();
  const url = `${config.baseURL || ""}${config.url || ""}`;
  const params = JSON.stringify(sortValue(config.params || {}));
  const credentials = config.withCredentials ? "auth" : "anon";
  return [method, url, params, credentials].join("::");
};

const cloneCachedResponse = (entry, config) => ({
  ...entry.response,
  data: structuredClone(entry.response.data),
  headers: { ...entry.response.headers },
  config,
  request: entry.response.request,
});

axios.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();

  if (!SAFE_METHODS.has(method)) {
    const csrfToken = getCookie("csrfToken");

    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers["x-csrf-token"] = csrfToken;
    }

    responseCache.clear();
    return config;
  }

  if (config.cache === false || config.skipCache) {
    return config;
  }

  const ttlMs = Number(config.cacheTtlMs ?? DEFAULT_GET_CACHE_TTL_MS);
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    return config;
  }

  const cacheKey = buildCacheKey(config);
  const cachedEntry = responseCache.get(cacheKey);

  if (cachedEntry && Date.now() - cachedEntry.timestamp < ttlMs) {
    config.adapter = async () => cloneCachedResponse(cachedEntry, config);
    return config;
  }

  config.metadata = {
    ...(config.metadata || {}),
    cacheKey,
    cacheTtlMs: ttlMs,
  };

  return config;
});

axios.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || "get").toLowerCase();
    if (!SAFE_METHODS.has(method)) {
      return response;
    }

    if (response.config?.cache === false || response.config?.skipCache) {
      return response;
    }

    const cacheKey = response.config?.metadata?.cacheKey;
    if (!cacheKey) {
      return response;
    }

    responseCache.set(cacheKey, {
      timestamp: Date.now(),
      response: {
        data: structuredClone(response.data),
        status: response.status,
        statusText: response.statusText,
        headers: { ...response.headers },
        request: response.request,
      },
    });

    return response;
  },
  (error) => Promise.reject(error),
);
